/**
 * Onboarding state — dismissal, lane choice and derived setup progress.
 *
 * All persistence is localStorage; nothing here calls the API. Callers pass in
 * the counts they already have, so this hook never couples to a data layer.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  SETUP_STEPS,
  SETUP_SNOOZE_DAYS,
  laneOrder,
  orgKey,
  userKey,
  type OverlayLane,
  type SetupStepCopy,
  type SetupStepId,
} from './overlayCopy';

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* no-op where storage is unavailable */
  }
};

/* ------------------------------------------------------------------ */
/* Seen / dismissed                                                    */
/* ------------------------------------------------------------------ */

export const isSeen = (id: string): boolean => read(userKey(id)) === '1';
export const markSeen = (id: string): void => write(userKey(id), '1');

/** Clear every overlay flag — powers "Restart the tour" in Settings. */
export const resetAllOverlays = (): void => {
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith('admeru_overlay__'))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* no-op */
  }
};

export function useSeen(id: string) {
  const [seen, setSeenState] = useState(() => isSeen(id));
  const setSeen = useCallback(() => {
    markSeen(id);
    setSeenState(true);
  }, [id]);
  return { seen, setSeen };
}

/* ------------------------------------------------------------------ */
/* Lane choice                                                         */
/* ------------------------------------------------------------------ */

const LANE_KEY = 'lane';

export const getLane = (): OverlayLane =>
  (read(userKey(LANE_KEY)) as OverlayLane) || 'studio';

export const setLane = (lane: OverlayLane): void =>
  write(userKey(LANE_KEY), lane);

/* ------------------------------------------------------------------ */
/* Setup dock                                                          */
/* ------------------------------------------------------------------ */

export interface SetupFacts {
  /** Brand kit has at least a logo or a non-default palette. */
  hasBrandKit: boolean;
  creativeCount: number;
  connectionCount: number;
  publishedCount: number;
}

export interface SetupStep extends SetupStepCopy {
  done: boolean;
  /** The first step that is not done. */
  active: boolean;
}

export interface SetupState {
  steps: SetupStep[];
  doneCount: number;
  total: number;
  complete: boolean;
  /** Dock should render at all. */
  visible: boolean;
  expanded: boolean;
  setExpanded: (next: boolean) => void;
  /** "Hide until I'm ready" — comes back after SETUP_SNOOZE_DAYS. */
  snooze: () => void;
}

const doneFor = (id: SetupStepId, f: SetupFacts): boolean => {
  switch (id) {
    case 'brand_kit':
      return f.hasBrandKit;
    case 'first_creative':
      return f.creativeCount > 0;
    case 'connect_account':
      return f.connectionCount > 0;
    case 'first_publish':
      return f.publishedCount > 0;
    default:
      return false;
  }
};

/**
 * The first setup step that isn't done yet, in the current lane's order — what
 * the handoff nudge points the user at after a page tour. Null once set up.
 */
export function nextSetupStep(facts: SetupFacts): SetupStepCopy | null {
  const order = laneOrder[getLane()];
  for (const id of order) {
    const copy = SETUP_STEPS.find((s) => s.id === id);
    if (copy && !doneFor(id, facts)) return copy;
  }
  return null;
}

export function useSetupDock(orgId: string, facts: SetupFacts): SetupState {
  const snoozeKey = orgKey('setup_snooze', orgId);
  const expandKey = orgKey('setup_expanded', orgId);

  const [snoozedUntil, setSnoozedUntil] = useState(() =>
    Number(read(snoozeKey) || 0)
  );
  const [expanded, setExpandedState] = useState(() => {
    const stored = read(expandKey);
    // Expanded on the very first session, collapsed thereafter.
    return stored === null ? true : stored === '1';
  });

  const lane = getLane();

  const steps = useMemo<SetupStep[]>(() => {
    const ordered = laneOrder[lane]
      .map((id) => SETUP_STEPS.find((s) => s.id === id))
      .filter((s): s is SetupStepCopy => Boolean(s));
    let activeAssigned = false;
    return ordered.map((copy) => {
      const done = doneFor(copy.id, facts);
      const active = !done && !activeAssigned;
      if (active) activeAssigned = true;
      return { ...copy, done, active };
    });
  }, [lane, facts]);

  const doneCount = steps.filter((s) => s.done).length;
  const complete = doneCount === steps.length;

  const setExpanded = useCallback(
    (next: boolean) => {
      write(expandKey, next ? '1' : '0');
      setExpandedState(next);
    },
    [expandKey]
  );

  const snooze = useCallback(() => {
    const until = Date.now() + SETUP_SNOOZE_DAYS * 864e5;
    write(snoozeKey, String(until));
    setSnoozedUntil(until);
  }, [snoozeKey]);

  return {
    steps,
    doneCount,
    total: steps.length,
    complete,
    visible: !complete && Date.now() > snoozedUntil,
    expanded,
    setExpanded,
    snooze,
  };
}
